import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Approveimg from "../../assets/images/Approvd.png";
import Deniedimg from "../../assets/images/Denied.png";
import { approveTicket, GetAllTickets, GetUserTickets } from "../../services/ticket";
import { GetDepartmentCoursesByDepartmentId } from "../../services/human-resource";
import { alpha, useTheme } from "@mui/material/styles";

export const ThankYouTicket = () => {
  const theme = useTheme();
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const [hasTasks, setHasTasks] = useState(false);

  const action = useMemo(() => {
    const actionFromUrl = searchParams.get("action");

    if (actionFromUrl) return actionFromUrl;

    // old support, if any old mail link still sends ?approved=true / false
    const oldApproved = searchParams.get("approved");
    if (oldApproved === "true") return "Approve";
    if (oldApproved === "false") return "Deny";

    return "Approve";
  }, [searchParams]);

  const cleanAction = action.toString().trim();
  const isApproveAction = cleanAction.toLowerCase() === "approve";
  const isDenyOrVoid =
    cleanAction.toLowerCase() === "deny" ||
    cleanAction.toLowerCase() === "void";

  const [ticket, setTicket] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [assignedToId, setAssignedToId] = useState("");
  const [approvedCompletionDate, setApprovedCompletionDate] = useState("");
  const [actionReason, setActionReason] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const actionByIdFromMail = Number(searchParams.get("actionById") || 0);
  const actionById = actionByIdFromMail || null;
  const token =
    localStorage.getItem("userToken_http://localhost:18344/v1.0/");

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const getTicketDepartmentId = (item = {}) =>
    item.departmentId ||
    item.DepartmentId ||
    item.assignedToDepartmentId ||
    item.AssignedToDepartmentId ||
    "";

  const loadTicketAndEmployees = async () => {
    try {
      setLoading(true);
      setMessage("");
      setError("");
      setSessionExpired(false);

      if (!token) {
        setTicket(null);
        setSessionExpired(true);
        setMessage("Session has been expired.");
        return;
      }

      if (!actionById) {
        setTicket(null);
        setSessionExpired(true);
        setMessage("Approval user id missing in mail link.");
        return;
      }

      const payload = {
        start: 0,
        length: 1000,
        searchKeyword: "",
        sortExpression: "Id desc",
        data: {
          employeename: user?.name,
          employeeId: actionById,
          tabValue: 4,
        },
      };

      const response = await GetUserTickets(payload);

      const list = response?.data?.result || [];
      const foundTicket = list.find(
        (x) => Number(x.id || x.Id) === Number(ticketId)
      );

      if (!foundTicket) {
        setTicket(null);
        setMessage("Request not found or already processed.");
        return;
      }

      setTicket(foundTicket);

      let requestHasTasks =
        Boolean(foundTicket.hasTasks || foundTicket.HasTasks) ||
        Number(foundTicket.taskCount || foundTicket.TaskCount || 0) > 0;

      setHasTasks(requestHasTasks);

      if (isApproveAction && !requestHasTasks) {
        const departmentId = getTicketDepartmentId(foundTicket);

        if (departmentId) {
          const empResponse = await GetDepartmentCoursesByDepartmentId(departmentId);

          if (empResponse?.status === 200) {
            setEmployees(empResponse.data?.buddyDropDowns || []);
          }
        }
      }
    } catch (err) {
      console.error(err);

      if (err?.response?.status === 401) {
        setTicket(null);
        setSessionExpired(true);
        setMessage("Session has been expired.");
        return;
      }

      setMessage("Unable to load request details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      loadTicketAndEmployees();
    }
  }, [ticketId]);

  const validate = () => {
    if (isApproveAction) {
      if (!hasTasks && !assignedToId) {
        return "Please select employee.";
      }

      if (!approvedCompletionDate) {
        return "Please select approved completion date.";
      }
    }

    if (isDenyOrVoid && !actionReason.trim()) {
      return `Please enter ${cleanAction.toLowerCase()} reason.`;
    }

    return "";
  };

  const getRecordType = (item = {}) => {
    const explicitType =
      item.recordType ||
      item.RecordType ||
      item.recordtype ||
      item.type ||
      item.Type ||
      "";

    if (explicitType) {
      return explicitType.toString().toLowerCase().trim();
    }

    const taskNo =
      item.taskNo ||
      item.TaskNo ||
      item.taskNumber ||
      item.TaskNumber ||
      "";

    const requestId =
      item.requestId ||
      item.RequestId ||
      item.parentRequestId ||
      item.ParentRequestId ||
      item.oldTicketReference ||
      item.OldTicketReference ||
      0;

    // Task rows usually have TaskNo or RequestId reference
    if (taskNo || Number(requestId) > 0) {
      return "task";
    }

    if (item.requestTypeName === "Internal Task") {
      return "task";
    }

    return "ticket";
  };

  const getItemRequestId = (item = {}) =>
    item.requestId ||
    item.RequestId ||
    item.parentRequestId ||
    item.ParentRequestId ||
    item.oldTicketReference ||
    item.OldTicketReference ||
    0;

  const checkRequestHasTasks = (list, requestId) => {
    const reqId = Number(requestId);

    return (list || []).some((item) => {
      const taskNo =
        item.taskNo ||
        item.TaskNo ||
        item.taskNumber ||
        item.TaskNumber ||
        "";

      const taskRequestId =
        item.requestId ||
        item.RequestId ||
        item.parentRequestId ||
        item.ParentRequestId ||
        item.oldTicketReference ||
        item.OldTicketReference ||
        0;

      const recordType = getRecordType(item);

      return (
        (recordType === "task" || !!taskNo) &&
        Number(taskRequestId) === reqId
      );
    });
  };

  const submitApproval = async () => {
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const response = await approveTicket(
        ticketId,
        isApproveAction && !hasTasks ? assignedToId : null,
        cleanAction,
        actionById,
        isDenyOrVoid ? actionReason.trim() : "",
        isApproveAction ? approvedCompletionDate : null
      );

      if (response?.status === 200 && response?.data?.success !== false) {
        setSuccess(true);
        setMessage(response?.data?.message || `Request ${cleanAction} successfully.`);
      } else {
        setSuccess(false);
        setMessage(response?.data?.message || "Request already processed or not found.");
      }
    } catch (err) {
      console.error(err);
      setSuccess(false);
      setMessage(
        err?.response?.data?.message ||
        "Something went wrong while processing request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (success !== null) {
    const isActionSuccessful = success === true;
    const resultImage =
      isActionSuccessful && isApproveAction ? Approveimg : Deniedimg;

    const resultAlt =
      isActionSuccessful && isApproveAction
        ? "approved"
        : cleanAction.toLowerCase() === "void"
          ? "voided"
          : "denied";

    const resultColor =
      isActionSuccessful && isApproveAction ? "success.main" : "error.main";

    return (
      <Box sx={{ minHeight: "100vh", textAlign: "center" }}>
        <img
          src={resultImage}
          alt={resultAlt}
          style={{ width: "100%", minHeight: "100vh", objectFit: "cover" }}
        />

        {message && (
          <Typography
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 30,
              fontWeight: 700,
              color: resultColor,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  if (sessionExpired) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: alpha(theme.palette.primary.main, 0.035),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 460,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 22px 60px rgba(15, 23, 42, 0.16)",
            border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              height: 6,
              bgcolor: "error.main",
            }}
          />

          <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 } }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 38,
                fontWeight: 900,
              }}
            >
              !
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 24, sm: 28 },
                fontWeight: 900,
                color: "text.primary",
                mb: 1,
              }}
            >
              Session Expired
            </Typography>

            <Typography
              sx={{
                fontSize: 15,
                color: "text.secondary",
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              Session has been expired. Please login again to approve, deny, or void this request.
            </Typography>

            <Box
              sx={{
                px: 1.5,
                py: 1,
                mb: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "primary.main",
                }}
              >
                REQ-{ticketId}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                const returnUrl = `${window.location.pathname}${window.location.search}`;
                window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
              }}
              sx={{
                py: 1.2,
                borderRadius: 2,
                fontWeight: 900,
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              Go to Login
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: alpha(theme.palette.primary.main, 0.035),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: 4,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 620,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: { xs: 2.5, sm: 3.5 },
            pb: 2,
            borderTop: `6px solid ${theme.palette.primary.main}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 28 },
                  fontWeight: 900,
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                Request {cleanAction}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.6,
                py: 0.8,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 900,
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              REQ-{ticketId}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pb: { xs: 2.5, sm: 3.5 } }}>
          {ticket && (
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.045),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Request Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "120px 1fr" },
                  rowGap: 0.8,
                  columnGap: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Reason
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", fontWeight: 800 }}
                >
                  {ticket.reason || ticket.Reason || "N/A"}
                </Typography>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Priority
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "primary.main", fontWeight: 900 }}
                >
                  {ticket.priority || ticket.Priority || "N/A"}
                </Typography>
              </Box>

              {hasTasks && isApproveAction && (
                <Box
                  sx={{
                    mt: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "success.main",
                      fontWeight: 800,
                    }}
                  >
                    Task already created. Only completion date is required.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {isApproveAction && (
            <>
              {!hasTasks && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Employee"
                  value={assignedToId}
                  onChange={(e) => setAssignedToId(e.target.value)}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  {employees.map((emp) => {
                    const id =
                      emp.personId ||
                      emp.personID ||
                      emp.id ||
                      emp.Id ||
                      emp.employeeId;

                    const name =
                      emp.employeeNameWithId ||
                      emp.employeeName ||
                      emp.buddyName ||
                      emp.name ||
                      emp.EmployeeName ||
                      `Employee ${id}`;

                    return (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}

              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Approved Completion Date"
                value={approvedCompletionDate}
                onChange={(e) => setApprovedCompletionDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              />
            </>
          )}

          {isDenyOrVoid && (
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              label={`${cleanAction} Reason`}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "primary.main",
                },
              }}
            />
          )}

          {error && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              }}
            >
              <Typography color="error.main" fontWeight={800} fontSize={14}>
                {error}
              </Typography>
            </Box>
          )}

          {message && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              }}
            >
              <Typography color="error.main" fontWeight={800} fontSize={14}>
                {message}
              </Typography>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={submitting || !ticket}
            onClick={submitApproval}
            color={
              isApproveAction
                ? "primary"
                : cleanAction.toLowerCase() === "void"
                  ? "secondary"
                  : "error"
            }
            sx={{
              mt: 0.5,
              py: 1.25,
              borderRadius: 2,
              fontWeight: 900,
              textTransform: "none",
              fontSize: 15,
              boxShadow: "none",
              "&:hover": {
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {submitting ? "Processing..." : `Submit ${cleanAction}`}
          </Button>

          <Typography
            sx={{
              mt: 2,
              textAlign: "center",
              color: "text.secondary",
              fontSize: 12,
            }}
          >
            This action will update the request approval status.
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};